import React, { useEffect, useRef, ReactNode } from 'react';
import { rafThrottle, addPassiveEventListener } from '../../utils/performance';

interface GlowCardProps {
  children: ReactNode;
  className?: string;
  glowColor?: 'blue' | 'purple' | 'green' | 'red' | 'orange';
  size?: 'sm' | 'md' | 'lg';
  width?: string | number;
  height?: string | number;
  customSize?: boolean; // When true, ignores size prop and uses width/height or className
}

const glowColorMap = {
  blue: { base: 220, spread: 200 },
  purple: { base: 280, spread: 300 },
  green: { base: 120, spread: 200 },
  red: { base: 0, spread: 200 },
  orange: { base: 30, spread: 200 }
};

const sizeMap = {
  sm: 'w-48 h-64',
  md: 'w-64 h-80',
  lg: 'w-80 h-96'
};

const GlowCard: React.FC<GlowCardProps> = ({ 
  children, 
  className = '', 
  glowColor = 'blue',
  size = 'md',
  width,
  height,
  customSize = false
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Detect device capabilities
    const isDesktop = window.innerWidth >= 1024;
    const isHighPerformance = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // Enable glow effects on desktop and tablet, but with different performance levels
    // Only skip on mobile (< 768px) or if user prefers reduced motion
    if (window.innerWidth < 768 || !isHighPerformance) {
      return;
    }

    let isVisible = false;
    let lastUpdateTime = 0;
    // Less aggressive throttling for better responsiveness
    const UPDATE_INTERVAL = isDesktop ? 8 : 16; // ~120fps on desktop, ~60fps on tablet

    // Intersection Observer to only animate when visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
          if (!isVisible && cardRef.current) {
            // Reset glow when not visible
            cardRef.current.style.setProperty('--intensity', '0');
          }
        });
      },
      { 
        rootMargin: '100px',
        threshold: 0.1 
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    // Optimized pointer move handler with better throttling
    const syncPointer = rafThrottle((e: MouseEvent) => {
      // Skip if not visible or performance budget exceeded
      const now = performance.now();
      if (!isVisible || (now - lastUpdateTime < UPDATE_INTERVAL)) {
        return;
      }
      
      lastUpdateTime = now;
      const { clientX: x, clientY: y } = e;
      
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        
        // Quick bounds check - more generous bounds for better glow visibility
        const glowRadius = isDesktop ? 200 : 150;
        if (x < rect.left - glowRadius || x > rect.right + glowRadius || 
            y < rect.top - glowRadius || y > rect.bottom + glowRadius) {
          // Far from element, set intensity to 0
          cardRef.current.style.setProperty('--intensity', '0');
          return;
        }
        
        const relativeX = x - rect.left;
        const relativeY = y - rect.top;
        
        // Optimized distance calculation using squared distance (no sqrt)
        const centerX = rect.width * 0.5;
        const centerY = rect.height * 0.5;
        const dx = relativeX - centerX;
        const dy = relativeY - centerY;
        const distanceSquared = dx * dx + dy * dy;
        const maxDistanceSquared = (centerX * centerX + centerY * centerY);
        
        // Calculate intensity without expensive operations
        const intensity = Math.max(0, Math.min(1, 1 - (distanceSquared / maxDistanceSquared)));
        
        // Use faster string concatenation
        cardRef.current.style.setProperty('--rx', relativeX.toString());
        cardRef.current.style.setProperty('--ry', relativeY.toString());
        // Boost intensity for more glowy effect
        const boostedIntensity = intensity * 1.5;
        cardRef.current.style.setProperty('--intensity', boostedIntensity.toFixed(1));
      }
    });

    // Use passive event listeners for better performance
    const removeEventListener = addPassiveEventListener<MouseEvent>(document, 'mousemove', syncPointer);

    return () => {
      observer.disconnect();
      removeEventListener();
      syncPointer.cancel();
    };
  }, []);

  const { base, spread } = glowColorMap[glowColor];

  // Determine sizing
  const getSizeClasses = () => {
    if (customSize) {
      return ''; // Let className or inline styles handle sizing
    }
    return sizeMap[size];
  };

  const getInlineStyles = () => {
    const baseStyles: React.CSSProperties & Record<string, any> = {
      '--base': base,
      '--spread': spread,
      '--radius': '14',
      '--border': '4',
      '--backdrop': 'hsl(0 0% 60% / 0.05)',
      '--backup-border': 'var(--backdrop)',
      '--size': '400',
      '--outer': '1',
      '--border-size': 'calc(var(--border, 2) * 1px)',
      '--spotlight-size': 'calc(var(--size, 150) * 1px)',
      '--hue': 'calc(var(--base) + (var(--xp, 0) * var(--spread, 0)))',
      backgroundImage: `radial-gradient(
        var(--spotlight-size) var(--spotlight-size) at
        calc(var(--rx, 0) * 1px)
        calc(var(--ry, 0) * 1px),
        hsl(var(--hue, 210) calc(var(--saturation, 100) * 1%) calc(var(--lightness, 70) * 1%) / calc(var(--intensity, 0) * 0.7)), transparent
      )`,
      backgroundColor: customSize ? 'transparent' : 'var(--backdrop, transparent)',
      backgroundSize: 'calc(100% + (2 * var(--border-size))) calc(100% + (2 * var(--border-size)))',
      backgroundPosition: '50% 50%',
      border: customSize ? 'var(--border-size) solid transparent' : 'var(--border-size) solid var(--backup-border)',
      position: 'relative' as const,
      touchAction: 'none' as const,
    };

    // Add width and height if provided
    if (width !== undefined) {
      baseStyles.width = typeof width === 'number' ? `${width}px` : width;
    }
    if (height !== undefined) {
      baseStyles.height = typeof height === 'number' ? `${height}px` : height;
    }

    return baseStyles;
  };

  const beforeAfterStyles = `
    [data-glow] {
      contain: layout style paint;
      transform: translate3d(0, 0, 0);
    }
    
    [data-glow]::before,
    [data-glow]::after {
      pointer-events: none;
      content: "";
      position: absolute;
      inset: calc(var(--border-size) * -1);
      border: var(--border-size) solid transparent;
      border-radius: calc(var(--radius) * 1px);
      background-size: calc(100% + (2 * var(--border-size))) calc(100% + (2 * var(--border-size)));
      background-repeat: no-repeat;
      background-position: 50% 50%;
      mask: linear-gradient(transparent, transparent), linear-gradient(white, white);
      mask-clip: padding-box, border-box;
      mask-composite: intersect;
    }
    
    [data-glow]::before {
      background-image: radial-gradient(
        calc(var(--spotlight-size) * 0.75) calc(var(--spotlight-size) * 0.75) at
        calc(var(--rx, 0) * 1px)
        calc(var(--ry, 0) * 1px),
        hsl(var(--hue, 210) calc(var(--saturation, 100) * 1%) calc(var(--lightness, 40) * 1%) / calc(var(--intensity, 0) * 3.5)), transparent 100%
      );
      filter: brightness(3.5);
    }
    
    [data-glow]::after {
      background-image: radial-gradient(
        calc(var(--spotlight-size) * 0.4) calc(var(--spotlight-size) * 0.4) at
        calc(var(--rx, 0) * 1px)
        calc(var(--ry, 0) * 1px),
        hsl(210 100% 90% / calc(var(--intensity, 0) * 2.8)), transparent 100%
      );
    }
    
    [data-glow] [data-glow] {
      position: absolute;
      inset: 0;
      will-change: filter;
      opacity: var(--outer, 1);
      border-radius: calc(var(--radius) * 1px);
      border-width: calc(var(--border-size) * 20);
      filter: blur(calc(var(--border-size) * 8));
      background: radial-gradient(
        calc(var(--spotlight-size) * 1.2) calc(var(--spotlight-size) * 1.2) at
        calc(var(--rx, 0) * 1px)
        calc(var(--ry, 0) * 1px),
        hsl(var(--hue, 210) calc(var(--saturation, 100) * 1%) calc(var(--lightness, 40) * 1%) / calc(var(--intensity, 0) * 1.5)), transparent 100%
      );
      pointer-events: none;
      border: none;
    }
    
    [data-glow] > [data-glow]::before {
      inset: -15px;
      border-width: 15px;
    }

    /* Enhanced border glow - more glowy */
    [data-glow]::before {
      box-shadow: 
        0 0 40px hsl(var(--hue, 210) 100% 60% / calc(var(--intensity, 0) * 1.2)),
        0 0 60px hsl(var(--hue, 210) 100% 70% / calc(var(--intensity, 0) * 0.6));
    }
    
    [data-glow]::after {
      box-shadow: 
        0 0 25px hsl(210 100% 90% / calc(var(--intensity, 0) * 1.5)),
        0 0 50px hsl(210 100% 95% / calc(var(--intensity, 0) * 0.8));
    }

    /* Reduced motion support */
    @media (prefers-reduced-motion: reduce) {
      [data-glow]::before,
      [data-glow]::after,
      [data-glow] [data-glow] {
        background: none !important;
        filter: none !important;
        box-shadow: none !important;
      }
    }

    /* Performance optimizations for mobile */
    @media (max-width: 767px) {
      [data-glow]::before,
      [data-glow]::after,
      [data-glow] [data-glow] {
        display: none;
      }
    }
    
    /* Simplified glow for tablets */
    @media (min-width: 768px) and (max-width: 1023px) {
      [data-glow] [data-glow] {
        filter: blur(calc(var(--border-size) * 2));
      }
      [data-glow]::before,
      [data-glow]::after {
        filter: brightness(1.2);
      }
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: beforeAfterStyles }} />
      <div
        ref={cardRef}
        data-glow
        style={getInlineStyles()}
        className={`
          ${getSizeClasses()}
          ${!customSize ? 'aspect-[3/4]' : ''}
          ${customSize ? 'rounded-[12px]' : 'rounded-2xl'} 
          relative 
          ${customSize ? '' : 'grid grid-rows-[1fr_auto] p-4 gap-4'} 
          ${customSize ? '' : 'shadow-[0_1rem_2rem_-1rem_black]'} 
          ${customSize ? '' : 'backdrop-blur-[5px]'}
          contain-all transform-gpu
          ${className}
        `}
      >
        <div ref={innerRef} data-glow></div>
        {children}
      </div>
    </>
  );
};

export { GlowCard }